import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../hooks/useApi";
import {
  Settings as SettingsIcon,
  Plus,
  Trash2,
  Key,
  Copy,
  Check,
  X,
  Pause,
  Play,
  Clock,
} from "lucide-react";
import { PageHeader } from "../components/PageHeader";

interface ApiKey {
  id: string;
  name: string;
  created_at: string;
  last_used_at?: string;
  expires_at?: string;
  is_active: boolean;
  permissions: string[];
}

export function Settings() {
  const apiClient = useApi();
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");

  // Check-in settings state
  const [checkinHour, setCheckinHour] = useState(7);
  const [checkinMinute, setCheckinMinute] = useState(0);

  // Fetch API keys
  const { data: apiKeysData, isLoading: keysLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: () => apiClient.get("/api/v1/api-keys"),
  });

  const apiKeys = (apiKeysData as any)?.api_keys || [];

  // Fetch user settings
  const { data: userSettingsData } = useQuery({
    queryKey: ["user-settings"],
    queryFn: () => apiClient.get("/api/v1/settings"),
  });

  const userSettings = (userSettingsData as any)?.data;

  // Update user settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (settings: {
      dailyCheckinHour: number;
      dailyCheckinMinute: number;
      timezone: string;
    }) => apiClient.patch("/api/v1/settings", settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-settings"] });
      queryClient.invalidateQueries({ queryKey: ["status"] }); // Refresh status to show new check-in time
    },
  });

  // Handle updating check-in settings
  const handleUpdateSettings = () => {
    updateSettingsMutation.mutate({
      dailyCheckinHour: checkinHour,
      dailyCheckinMinute: checkinMinute,
      timezone: userTimezone,
    });
  };

  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Update state when user settings are loaded
  React.useEffect(() => {
    if (userSettings) {
      setCheckinHour(userSettings.dailyCheckinHour || 7);
      setCheckinMinute(userSettings.dailyCheckinMinute || 0);
    }
  }, [userSettings]);

  // Create API key mutation
  const createKeyMutation = useMutation({
    mutationFn: (name: string) => apiClient.post("/api/v1/api-keys", { name }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      setShowCreateForm(false);
      setNewKeyName("");
      const data = response as any;
      setCreatedApiKey(data.key);
      setShowApiKeyModal(true);
      setCopiedKey(false);
    },
  });

  // Toggle API key status mutation
  const toggleKeyMutation = useMutation({
    mutationFn: (keyId: string) => apiClient.patch(`/api/v1/api-keys/${keyId}/toggle`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });

  // Delete API key mutation (only for inactive keys)
  const deleteKeyMutation = useMutation({
    mutationFn: (keyId: string) => apiClient.delete(`/api/v1/api-keys/${keyId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });

  const handleCreateKey = (e: React.FormEvent) => {
    e.preventDefault();
    if (newKeyName.trim()) {
      createKeyMutation.mutate(newKeyName.trim());
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        description="Manage your account settings"
        icon={SettingsIcon}
      />

      {/* API Keys */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Key className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg leading-6 font-medium text-gray-900">API Keys</h3>
            </div>
            {!showCreateForm && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create API Key
              </button>
            )}
          </div>

          {/* Create API Key Form */}
          {showCreateForm && (
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h4 className="text-md leading-6 font-medium text-gray-900 mb-4">Create New API Key</h4>
              <form onSubmit={handleCreateKey} className="space-y-4">
                <div>
                  <label htmlFor="keyName" className="block text-sm font-medium text-gray-700">
                    Key Name
                  </label>
                  <input
                    type="text"
                    id="keyName"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                    placeholder="e.g., Mobile App, Script, etc."
                    required
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    Give your API key a descriptive name to remember its purpose.
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={createKeyMutation.isPending}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {createKeyMutation.isPending ? "Creating..." : "Create Key"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewKeyName("");
                    }}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Cancel
                  </button>
                </div>
              </form>

              {createKeyMutation.isError && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-sm text-red-700">
                    Failed to create API key: {createKeyMutation.error?.message || "Unknown error"}
                  </p>
                </div>
              )}
            </div>
          )}

          {keysLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8">
              <Key className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No API keys</h3>
              <p className="mt-1 text-sm text-gray-500">
                Create your first API key to enable programmatic access to check-ins.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {apiKeys.map((key: ApiKey) => (
                <div
                  key={key.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <Key className="h-4 w-4 text-gray-400 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{key.name}</p>
                        <p className="text-xs text-gray-500">
                          Created {new Date(key.created_at).toLocaleDateString()}
                          {key.last_used_at &&
                            ` • Last used ${new Date(key.last_used_at).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        key.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {key.is_active ? "Active" : "Inactive"}
                    </span>
                    <button
                      onClick={() => toggleKeyMutation.mutate(key.id)}
                      disabled={toggleKeyMutation.isPending}
                      className={`p-1 rounded focus:outline-none focus:ring-2 ${
                        key.is_active
                          ? "text-gray-400 hover:text-yellow-600 focus:ring-yellow-500"
                          : "text-gray-400 hover:text-green-600 focus:ring-green-500"
                      }`}
                      title={key.is_active ? "Deactivate API key" : "Activate API key"}
                    >
                      {key.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </button>
                    {!key.is_active && (
                      <button
                        onClick={() => deleteKeyMutation.mutate(key.id)}
                        disabled={deleteKeyMutation.isPending}
                        className="p-1 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                        title="Delete API key"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start">
              <div className="flex-shrink-0 mb-3 sm:mb-0">
                <Key className="h-5 w-5 text-blue-400" />
              </div>
              <div className="sm:ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800 mb-2">API Key Usage</h3>
                <div className="text-sm text-blue-700 space-y-3">
                  <p>
                    Use your API keys to check in programmatically without using the web interface.
                    Include the API key in the Authorization header:{" "}
                    <code className="bg-blue-100 px-1 py-0.5 rounded text-xs break-all">
                      Bearer dm_xxx
                    </code>
                  </p>
                  <div>
                    <p className="font-medium mb-2">
                      <strong>Example:</strong>
                    </p>
                    <pre className="bg-blue-100 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap sm:whitespace-pre">
                      {`curl -X POST https://api.malive.caidu.me/api/v1/checkin \\
  -H "Authorization: Bearer dm_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"signalType": "manual", "metadata": {"source": "api"}}'`}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Check-in Settings */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-4">
            <Clock className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg leading-6 font-medium text-gray-900">Daily Check-in Settings</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="checkinTime" className="block text-sm font-medium text-gray-700">
                Daily Check-in Due Time
              </label>
              <p className="mt-1 text-sm text-gray-500">
                Set the time each day when your check-in becomes due. You'll receive reminders and
                escalations if you haven't checked in by this time.
              </p>

              <div className="mt-3 flex items-center space-x-2">
                <div className="flex items-center space-x-2">
                  <select
                    id="checkinHour"
                    value={checkinHour}
                    onChange={(e) => setCheckinHour(parseInt(e.target.value))}
                    className="block w-20 border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {i.toString().padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                  <span className="text-gray-500">:</span>
                  <select
                    id="checkinMinute"
                    value={checkinMinute}
                    onChange={(e) => setCheckinMinute(parseInt(e.target.value))}
                    className="block w-20 border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  >
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i}>
                        {i.toString().padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  onClick={handleUpdateSettings}
                  disabled={updateSettingsMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {updateSettingsMutation.isPending ? "Updating..." : "Update Settings"}
                </button>
              </div>
            </div>

            {updateSettingsMutation.isError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-700">
                  Failed to update settings:{" "}
                  {updateSettingsMutation.error?.message || "Unknown error"}
                </p>
              </div>
            )}

            {updateSettingsMutation.isSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <p className="text-sm text-green-700">Settings updated successfully!</p>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Clock className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800">How Daily Check-ins Work</h4>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      After you check in, your next check-in will be due at{" "}
                      {checkinHour.toString().padStart(2, "0")}:
                      {checkinMinute.toString().padStart(2, "0")} the following day. If you haven't
                      checked in by that time, you'll receive reminders and escalations may begin.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* API Key Modal */}
      {showApiKeyModal && createdApiKey && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 sm:w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Key className="h-5 w-5 mr-2 text-green-600" />
                  API Key Created
                </h3>
                <button
                  onClick={() => setShowApiKeyModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your API Key
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      readOnly
                      value={createdApiKey}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                    />
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(createdApiKey);
                          setCopiedKey(true);
                          setTimeout(() => setCopiedKey(false), 2000);
                        } catch (error) {
                          console.error("Failed to copy:", error);
                        }
                      }}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      {copiedKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                  {copiedKey && <p className="mt-1 text-sm text-green-600">Copied to clipboard!</p>}
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Key className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-yellow-800">
                        Important Security Notice
                      </h4>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          This API key will only be shown once. Save it securely as it cannot be
                          retrieved later. Anyone with this key can check in on your behalf.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setShowApiKeyModal(false)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    I Saved My Key
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
